import { Loader } from "lucide-react";
import { FC } from "react";

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useDeployContractMutation } from "@/data/mutations";
import { useDialogState } from "@/hooks/use-dialog-state";
import {
  GasInfoByNetwork,
  getGasDescription,
  Network,
  NetworkNameByChainId,
} from "@/lib/chains";
import { cn } from "@/lib/utils";

type Props = {
  skuId: string;
  tokenName: string;
  tokenSymbol: string;
  chainId: Network;
  currentGas?: number;
  gasLow?: boolean;
};

export const ConfirmBlockchainDialog: FC<Props> = ({
  skuId,
  tokenName,
  tokenSymbol,
  chainId,
  currentGas,
  gasLow,
}) => {
  const { dialog, closeDialog } = useDialogState();
  const deployContractMutation = useDeployContractMutation();

  const handleSubmit = () => {
    deployContractMutation.mutate({
      skuId,
      body: { tokenName, tokenSymbol, nftChainId: chainId },
    });
  };

  return (
    <AlertDialog open={dialog?.name === "confirm-blockchain"}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Confirm blockchain</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you would like to enable and deploy your Digital
            Product Passport to the blockchain?{" "}
            <i>This cannot be changed or reversed after clicking “Confirm”.</i>
          </AlertDialogDescription>
          {currentGas &&
            gasLow !== undefined &&
            GasInfoByNetwork[chainId as keyof typeof GasInfoByNetwork] && (
              <div className="flex flex-col items-end text-sm">
                <div className="mt-2 flex gap-2">
                  <p className="text-muted-foreground font-medium">
                    Blockchain:
                  </p>
                  <p className="font-medium w-[297px]">
                    {NetworkNameByChainId[chainId]}
                  </p>
                </div>
                <div className="mt-2 flex gap-2">
                  <p className="text-muted-foreground font-medium">Gas:</p>
                  <div
                    className={cn(
                      gasLow ? "text-green-600" : "text-destructive",
                    )}
                  >
                    <p className="font-medium">
                      {currentGas.toFixed(
                        GasInfoByNetwork[
                          chainId as keyof typeof GasInfoByNetwork
                        ].decimals,
                      )}{" "}
                      {
                        GasInfoByNetwork[
                          chainId as keyof typeof GasInfoByNetwork
                        ].unit
                      }
                    </p>
                    <p className="w-[297px]">
                      {getGasDescription(
                        chainId as keyof typeof GasInfoByNetwork,
                        gasLow,
                      )}
                    </p>
                  </div>
                </div>
              </div>
            )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button
            variant="outline"
            onClick={closeDialog}
            disabled={deployContractMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={deployContractMutation.isPending}
          >
            {deployContractMutation.isPending ? (
              <Loader className="animate-spin" />
            ) : null}
            Confirm
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
